// ============================================================
// UNNATURAL DOS - GM-switchable house rule for how Unnatural
// Characteristic multipliers affect Degrees of Success on
// characteristic-based d100 tests. One pure choke point shared
// by sheet tests, attack rolls, defence reactions, and BOTH
// probability engines (which must mirror the live math).
//
// Contract: called ONLY on successful tests. Pass/fail is
// always decided against the native target; DoF is never
// affected in any mode. Zero React dependency.
// ============================================================
import { calcDoS } from './dice';

export type UnnaturalDosMode = 'off' | 'half' | 'bonus' | 'effective';

/**
 * A table that has not chosen: Bonus-denominated (the user, 2026-10-20; the
 * core rules, `off`, until then). The resolver, the registry, the engine and
 * every hook's fallback read this one constant.
 */
export const DEFAULT_UNNATURAL_DOS_MODE: UnnaturalDosMode = 'bonus';

export interface UnnaturalDosEntry {
  key: UnnaturalDosMode;
  label: string;
  hint: string;
}

/**
 * The rulings on offer, in the order the GM sees them.
 *
 * `{key, label, hint}` like `COVER_CONFLICT_MODES` and `ARC_PRESETS`. It
 * used to be `{id, label, description}` alone among the three, which is
 * the sort of difference that costs nothing until something wants to
 * render all of them from one table (v8.58.0).
 */
export const UNNATURAL_DOS_MODES: UnnaturalDosEntry[] = [
  {
    key: 'off',
    label: 'Off (core rules)',
    hint: 'Multipliers affect bonuses and soak only - tests are unchanged.',
  },
  {
    key: 'half',
    label: 'Half multiplier (FFG errata)',
    hint: '+floor(×N ÷ 2) DoS on a passed test (×2 → +1, ×4 → +2).',
  },
  {
    key: 'bonus',
    label: 'Bonus-denominated',
    hint: '+(×N − 1) × characteristic bonus DoS on a passed test (Tgh 40 ×2 → +4).',
  },
  {
    key: 'effective',
    label: 'Effective value',
    hint: 'DoS counted against value × N; pass/fail still vs the native value.',
  },
];

const KEYS = new Set(UNNATURAL_DOS_MODES.map((m) => m.key));

/** A stored ruling is untrusted input - a later build may have written a mode this one lacks. */
export function isUnnaturalDosMode(value: unknown): value is UnnaturalDosMode {
  return typeof value === 'string' && KEYS.has(value as UnnaturalDosMode);
}

export interface UnnaturalDosResult {
  /** Final DoS after the house rule. */
  dos: number;
  /** DoS added by the rule (0 when off / ×1 / no gain). */
  bonus: number;
  /** Narrative note, e.g. "Unnatural ×2: +4 DoS (bonus rule)" - null when bonus is 0. */
  note: string | null;
}

/**
 * Apply the active Unnatural DoS mode to a successful test.
 *
 * @param dos          Base DoS from calcDoS against the native target
 * @param roll         The d100 roll (needed by 'effective' mode)
 * @param nativeTarget The (modified, clamped) target the roll succeeded against
 * @param charValue    The governing characteristic's native value
 * @param multiplier   Effective ×N (override ?? stored); <= 1 is a no-op
 * @param mode         Active house-rule mode
 */
export function applyUnnaturalDos(
  dos: number,
  roll: number,
  nativeTarget: number,
  charValue: number,
  multiplier: number,
  mode: UnnaturalDosMode,
): UnnaturalDosResult {
  const mult = Math.floor(multiplier || 1);
  if (mode === 'off' || mult <= 1) return { dos, bonus: 0, note: null };

  let bonus = 0;
  if (mode === 'half') {
    bonus = Math.floor(mult / 2);
  } else if (mode === 'bonus') {
    bonus = (mult - 1) * Math.floor(charValue / 10);
  } else {
    // 'effective': recount against the multiplied value, never below base
    bonus = Math.max(0, calcDoS(nativeTarget + (mult - 1) * charValue, roll) - dos);
  }

  if (bonus <= 0) return { dos, bonus: 0, note: null };
  return {
    dos: dos + bonus,
    bonus,
    note: `Unnatural ×${mult}: +${bonus} DoS (${mode} rule)`,
  };
}
